/**
 * Copyright (c) 2023 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 * @author Dominik Böck <d.boeck@techdivision.com>
 */

import * as Yup from 'yup';
import dayjs from 'dayjs';
import { checkoutFormModel } from './FormModel';
import { isIbanValid } from '../../Signals/formStateSignal';

const {
  formField: {
    studioId,
    gender,
    contract,
    firstName,
    lastName,
    street,
    houseNumber,
    postCode,
    city,
    email,
    phonePrefix,
    phone,
    dateOfBirth,
    iban,
    generalTerms,
    marketingTerms,
  },
} = checkoutFormModel;

const phoneRegExp = /^([1-9][0-9]*)+$/;

/**
 *
 * dynamic form validation schema
 */
export const useCheckoutFormValidatorGenerator = () => {
  const getStudioSchema = () => ({
    [studioId.name]: Yup.string().required(`${studioId.requiredErrorMsg}`),
  });
  const getContractSchema = () => ({
    [contract.name]: Yup.string().required(`${contract.requiredErrorMsg}`),
  });
  const getPersonalDataSchema = () => ({
    [firstName.name]: Yup.string()
      .required(`${firstName.requiredErrorMsg}`)
      .min(2, `${firstName.minCharCountErrorMsg}`),
    [lastName.name]: Yup.string()
      .required(`${lastName.requiredErrorMsg}`)
      .min(2, `${lastName.minCharCountErrorMsg}`),
    [gender.name]: Yup.string().required(`${gender.requiredErrorMsg}`),
    [street.name]: Yup.string().required(`${street.requiredErrorMsg}`),
    [houseNumber.name]: Yup.string().required(`${houseNumber.requiredErrorMsg}`),
    [postCode.name]: Yup.string().required(`${postCode.requiredErrorMsg}`),
    [city.name]: Yup.string().required(`${city.requiredErrorMsg}`),
    [email.name]: Yup.string()
      .required(`${email.requiredErrorMsg}`)
      .email(`${email.formatErrorMsg}`),
    [phonePrefix.name]: Yup.string().required(`${phonePrefix.requiredErrorMsg}`),
    [phone.name]: Yup.string()
      .required(`${phone.requiredErrorMsg}`)
      .matches(phoneRegExp, `${phone.formatErrorMsg}`)
      .min(5, `${phone.charCountErrorMsg}`)
      .max(15, `${phone.charCountErrorMsg}`),
    [dateOfBirth.name]: Yup.date()
      .required(dateOfBirth.requiredErrorMsg)
      .test((value) => dayjs().diff(dayjs(value), 'year') >= 18),
    [iban.name]: Yup.string().required(`${iban.requiredErrorMsg}`).test('iban-test', `${iban.notValidErrorMsg}`, () => isIbanValid.value),
    [generalTerms.name]: Yup.string().required(`${generalTerms.requiredErrorMsg}`),
    [marketingTerms.name]: Yup.string().required(`${marketingTerms.requiredErrorMsg}`),
  });

  const generateSchema = () => [
    Yup.object().shape(getStudioSchema()),
    Yup.object().shape(getContractSchema()),
    Yup.object().shape(getPersonalDataSchema()),
  ];
  return { generateSchema };
};
